import { ReactNode, useEffect, useRef, useState } from 'react';
import maplibregl, { Map, StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useWidgetNavigate } from '@/core/widgets/export';

type PipelinesFC = GeoJSON.FeatureCollection<GeoJSON.LineString | GeoJSON.MultiLineString>;
type RegionsFC = GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
type ZonesFC = GeoJSON.FeatureCollection<GeoJSON.Point>;
type NoticesFC = GeoJSON.FeatureCollection<GeoJSON.Point>;

type Severity = 'info' | 'warning' | 'critical';

type Notice = {
  id?: string;
  title?: string;
  summary?: string;
  url?: string;
  severity?: Severity;
  date?: string;
  postedAt?: string;
  assets?: string[];
};

type ActiveZone = {
  name: string;
  subtitle?: string;
  severity?: Severity;
  notices: Notice[];
  assetCount: number;
  x: number;
  y: number;
};

type PipelineMapProps = {
  pipelines?: PipelinesFC;
  regions?: RegionsFC;
  zones?: ZonesFC;
  notices?: NoticesFC;
  initialCenter?: [number, number];
  initialBBox?: [[number, number], [number, number]];
  initialZoom?: number;
  notFoundOverlay?: boolean; // temporary
  toolbar?: ReactNode;
  onCameraChange?: (camera: { center: [number, number]; zoom: number }) => void;
  focus?: { center: [number, number]; zoom?: number };
};

const emptyPipelines: PipelinesFC = { type: 'FeatureCollection', features: [] };
const emptyRegions: RegionsFC = { type: 'FeatureCollection', features: [] };
const emptyZones: ZonesFC = { type: 'FeatureCollection', features: [] };
const emptyNotices: NoticesFC = { type: 'FeatureCollection', features: [] };

const usBounds: [[number, number], [number, number]] = [
  [-127, 23],
  [-66, 51],
];

const TILE_SIZE = 512;
const BASE_MIN_ZOOM = 3;
const INITIAL_ZOOM_BOOST = 0.2;
const FALLBACK_ZOOM = 4.5;

function usCoverZoom(map: Map): number | undefined {
  const { clientWidth, clientHeight } = map.getContainer();
  if (!clientWidth || !clientHeight) return undefined;

  const [[west, south], [east, north]] = usBounds;
  const spanX = (east - west) / 360;
  const spanY =
    maplibregl.MercatorCoordinate.fromLngLat({ lng: 0, lat: south }).y -
    maplibregl.MercatorCoordinate.fromLngLat({ lng: 0, lat: north }).y;

  return Math.max(
    Math.log2(clientWidth / (spanX * TILE_SIZE)),
    Math.log2(clientHeight / (spanY * TILE_SIZE)),
  );
}

const severityColor: Record<string, string> = {
  info: '#3b82f6',
  warning: '#f59e0b',
  critical: '#ef4444',
};

const parseList = <T,>(raw: unknown): T[] => {
  if (Array.isArray(raw)) return raw as T[];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const minimalRasterStyle: StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: [
        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
        //  TO-DO, verify if we need/want more tiles
        // 'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
        // 'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '&copy; OpenStreetMap contributors',
    },
  },
  layers: [
    {
      id: 'osm',
      type: 'raster',
      source: 'osm',
      paint: {
        'raster-saturation': -1,
        'raster-contrast': -0.15,
        'raster-brightness-min': 0.15,
        'raster-brightness-max': 0.95,
      },
    },
  ],
};

export default function PipelineMap({
  pipelines = emptyPipelines,
  regions = emptyRegions,
  zones = emptyZones,
  notices = emptyNotices,
  toolbar,
  initialCenter,
  initialBBox,
  initialZoom,
  notFoundOverlay, // temporary
  onCameraChange,
  focus,
}: PipelineMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [activeZone, setActiveZone] = useState<ActiveZone | null>(null);

  const navigate = useWidgetNavigate();

  const dataRef = useRef({ pipelines, regions, zones, notices });
  dataRef.current = { pipelines, regions, zones, notices };

  const onCameraChangeRef = useRef(onCameraChange);
  onCameraChangeRef.current = onCameraChange;

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setActiveZone(null);
      closeTimerRef.current = null;
    }, 150);
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    const currentBBox = map.getBounds();

    if (initialCenter) {
      const [lng, lat] = initialCenter;
      if (currentCenter.lng !== lng || currentCenter.lat !== lat) map.setCenter(initialCenter);
    }
    if (currentZoom !== initialZoom) map.setZoom(initialZoom || 3.5);
    if (initialBBox) {
      const [[swLng, swLat], [neLng, neLat]] = initialBBox;
      if (
        currentBBox._sw.lng !== swLng ||
        currentBBox._sw.lat !== swLat ||
        currentBBox._ne.lng !== neLng ||
        currentBBox._ne.lat !== neLat
      ) {
        const chosenBounds = initialBBox || usBounds;
        console.log(
          `setting bound coordinates to ${chosenBounds[0].join(', ')} ${chosenBounds[1].join(', ')}`,
        );
        map.setMaxBounds(initialBBox || usBounds);
      }
    }
  }, [initialCenter, initialZoom, initialBBox]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: minimalRasterStyle,
      center: initialCenter ?? [-98.5, 39.5],
      zoom: initialZoom ?? FALLBACK_ZOOM,
      minZoom: 3,
      attributionControl: false,
    });

    const lockToUs = () => {
      const cover = usCoverZoom(map);
      if (cover === undefined) return;
      map.setMinZoom(Math.max(BASE_MIN_ZOOM, cover + 0.001));
    };

    lockToUs();
    map.setMaxBounds(usBounds);
    map.on('resize', lockToUs);

    if (!initialCenter && !initialZoom) {
      map.fitBounds(usBounds, { duration: 0 });
      map.setZoom(map.getZoom() + INITIAL_ZOOM_BOOST);
    }

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    const hovered: { source: string; ids: Array<number | string>; key: string } = {
      source: '',
      ids: [],
      key: '',
    };

    const clearHoverState = () => {
      if (hovered.source && hovered.ids.length > 0) {
        for (const id of hovered.ids) {
          map.setFeatureState({ source: hovered.source, id }, { hover: false });
        }
      }
      hovered.source = '';
      hovered.ids = [];
      hovered.key = '';
    };

    const hoverPriority: Array<{ layer: string; source: string }> = [
      { layer: 'pipelines-line', source: 'pipelines' },
      { layer: 'regions-fill', source: 'regions' },
    ];

    const collectMatchingIds = (
      source: string,
      sourceLayer: string | undefined,
      name: string | undefined,
      fallbackId: number | string | null,
    ): Array<number | string> => {
      if (!name) {
        return fallbackId !== null ? [fallbackId] : [];
      }
      const features = map.querySourceFeatures(source, {
        sourceLayer,
        filter: ['==', ['get', 'name'], name],
      });
      const ids = new Set<number | string>();
      for (const f of features) {
        if (f.id !== undefined && f.id !== null) ids.add(f.id);
      }
      if (ids.size === 0 && fallbackId !== null) ids.add(fallbackId);
      return Array.from(ids);
    };

    const onMapMouseMove = (e: maplibregl.MapMouseEvent) => {
      const tip = tooltipRef.current;

      // Zone points take priority and open a sticky card (notices + owned assets)
      // instead of the plain text tooltip.
      const zoneFeatures = map.queryRenderedFeatures(e.point, { layers: ['zones-point'] });
      if (zoneFeatures.length > 0) {
        const f = zoneFeatures[0];
        const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
        const pt = map.project(coords);
        cancelClose();
        clearHoverState();
        if (tip) tip.style.display = 'none';
        map.getCanvas().style.cursor = 'pointer';
        const pipelineName = f.properties?.pipelineName as string | undefined;
        const regionName = f.properties?.regionName as string | undefined;
        setActiveZone({
          name: (f.properties?.name as string) ?? 'Zone',
          subtitle: [pipelineName, regionName].filter(Boolean).join(' · ') || undefined,
          severity: f.properties?.severity as Severity | undefined,
          notices: parseList<Notice>(f.properties?.notices),
          assetCount: Number(f.properties?.assetCount ?? 0),
          x: pt.x,
          y: pt.y,
        });
        return;
      }

      // Not over a notice point: let any open card close (gives time to reach it).
      scheduleClose();

      let match: { feature: maplibregl.MapGeoJSONFeature; source: string } | null = null;
      for (const { layer, source } of hoverPriority) {
        const features = map.queryRenderedFeatures(e.point, { layers: [layer] });
        if (features.length > 0) {
          match = { feature: features[0], source };
          break;
        }
      }

      if (!match) {
        map.getCanvas().style.cursor = '';
        clearHoverState();
        if (tip) tip.style.display = 'none';
        return;
      }

      const name = match.feature.properties?.name as string | undefined;
      const fallbackId = match.feature.id ?? null;
      const key = `${match.source}::${name ?? `__id:${fallbackId}`}`;

      if (hovered.key !== key) {
        clearHoverState();
        const ids = collectMatchingIds(match.source, match.feature.sourceLayer, name, fallbackId);
        for (const id of ids) {
          map.setFeatureState({ source: match.source, id }, { hover: true });
        }
        hovered.source = match.source;
        hovered.ids = ids;
        hovered.key = key;
      }

      map.getCanvas().style.cursor = 'pointer';
      if (!tip) return;
      if (!name) {
        tip.style.display = 'none';
        return;
      }
      tip.textContent = name;
      tip.style.display = 'block';
      tip.style.left = `${e.point.x + 12}px`;
      tip.style.top = `${e.point.y + 12}px`;
    };

    const onMapMouseOut = () => {
      map.getCanvas().style.cursor = '';
      clearHoverState();
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
      scheduleClose();
    };

    map.on('load', () => {
      map.addSource('regions', {
        type: 'geojson',
        data: dataRef.current.regions,
        generateId: true,
      });
      map.addLayer({
        id: 'regions-fill',
        type: 'fill',
        source: 'regions',
        paint: {
          'fill-color': ['coalesce', ['get', 'color'], '#3b82f6'],
          'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.5, 0.18],
        },
      });
      map.addLayer({
        id: 'regions-outline',
        type: 'line',
        source: 'regions',
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#1d4ed8'],
          'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0.8],
          'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 2.5, 1.5],
        },
      });

      map.addSource('pipelines', {
        type: 'geojson',
        data: dataRef.current.pipelines,
        generateId: true,
      });
      map.addLayer({
        id: 'pipelines-line',
        type: 'line',
        source: 'pipelines',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ff6b35',
          'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 4, 2],
          'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0.75],
        },
      });

      map.addSource('zones', {
        type: 'geojson',
        data: dataRef.current.zones,
        generateId: true,
      });
      map.addLayer({
        id: 'zones-point',
        type: 'circle',
        source: 'zones',
        paint: {
          'circle-radius': [
            '+',
            ['case', ['boolean', ['feature-state', 'hover'], false], 2, 0],
            [
              'interpolate',
              ['linear'],
              ['coalesce', ['get', 'noticeCount'], 0],
              0,
              6,
              1,
              10,
              10,
              12,
              100,
              14,
            ],
          ],
          // Zones whose assets have notices stand out (amber); quiet zones are teal.
          'circle-color': ['case', ['boolean', ['get', 'hasNotices'], false], '#f59e0b', '#0d9488'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': ['case', ['boolean', ['feature-state', 'hover'], false], 2, 1],
          'circle-opacity': 0.9,
        },
      });

      map.addSource('notices', { type: 'geojson', data: dataRef.current.notices });
      map.addLayer({
        id: 'notices-point',
        type: 'circle',
        source: 'notices',
        paint: {
          'circle-radius': 6,
          'circle-color': [
            'match',
            ['get', 'severity'],
            'critical',
            severityColor.critical,
            'warning',
            severityColor.warning,
            'info',
            severityColor.info,
            '#ffffff',
          ],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
        },
      });

      map.on('mousemove', onMapMouseMove);
      map.on('mouseout', onMapMouseOut);
    });

    const reportCamera = () => {
      const { lng, lat } = map.getCenter();
      onCameraChangeRef.current?.({ center: [lng, lat], zoom: map.getZoom() });
    };
    map.on('moveend', reportCamera);
    map.on('zoomend', reportCamera);

    mapRef.current = map;

    return () => {
      cancelClose();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const source = map.getSource('pipelines') as maplibregl.GeoJSONSource | undefined;
    if (source) source.setData(pipelines);
  }, [pipelines]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const source = map.getSource('regions') as maplibregl.GeoJSONSource | undefined;
    if (source) source.setData(regions);
  }, [regions]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const source = map.getSource('zones') as maplibregl.GeoJSONSource | undefined;
    if (source) source.setData(zones);
  }, [zones]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const labels = zones.features.flatMap(feature => {
      const count = Number(feature.properties?.noticeCount ?? 0);
      if (!count || feature.geometry?.type !== 'Point') return [];

      const el = document.createElement('div');
      el.textContent = String(count);
      el.style.pointerEvents = 'none';
      el.style.font = '600 11px/1 system-ui, -apple-system, sans-serif';
      el.style.color = '#1f2937';
      el.style.userSelect = 'none';

      return new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat(feature.geometry.coordinates as [number, number])
        .addTo(map);
    });

    return () => labels.forEach(marker => marker.remove());
  }, [zones]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const source = map.getSource('notices') as maplibregl.GeoJSONSource | undefined;
    if (source) source.setData(notices);
  }, [notices]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;

    const [lng, lat] = focus.center;
    const current = map.getCenter();
    const settled =
      Math.abs(current.lng - lng) < 1e-4 &&
      Math.abs(current.lat - lat) < 1e-4 &&
      (focus.zoom === undefined || Math.abs(map.getZoom() - focus.zoom) < 0.01);
    if (settled) return;

    map.flyTo({ center: [lng, lat], zoom: focus.zoom ?? map.getZoom(), duration: 600 });
  }, [focus]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      {toolbar && <div className="   border-bottom">{toolbar}</div>}
      <div ref={containerRef} style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

        {notFoundOverlay === true && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 30,
              background: '#00000045',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 500,
              boxShadow: '0 4px 12px #00000025',
              pointerEvents: 'auto',
              backdropFilter: 'blur(2px)',
            }}
          >
            <span>Cannot find specified location</span>
          </div>
        )}

        <div
          ref={tooltipRef}
          style={{
            position: 'absolute',
            display: 'none',
            pointerEvents: 'none',
            background: 'rgba(17, 24, 39, 0.92)',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}
        />
        {activeZone &&
          (() => {
            const margin = 12;
            const cardWidth = 300;
            const cw = containerRef.current?.clientWidth ?? 0;
            const ch = containerRef.current?.clientHeight ?? 0;
            // Flip the card when there isn't enough room below / to the right of the point.
            const placeLeft = cw > 0 && activeZone.x + margin + cardWidth > cw;
            const spaceBelow = ch - activeZone.y - margin * 2;
            const spaceAbove = activeZone.y - margin * 2;
            const placeAbove = ch > 0 && spaceBelow < 200 && spaceAbove > spaceBelow;
            const availableHeight = placeAbove ? spaceAbove : spaceBelow;
            const maxHeight = Math.max(140, Math.min(420, availableHeight));
            // Notices and assets share one scroll region so the card never outgrows the map.
            const bodyMaxHeight = Math.max(96, maxHeight - (activeZone.subtitle ? 76 : 58));
            return (
              <div
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
                style={{
                  position: 'absolute',
                  left: placeLeft ? undefined : activeZone.x + margin,
                  right: placeLeft ? cw - activeZone.x + margin : undefined,
                  top: placeAbove ? undefined : activeZone.y + margin,
                  bottom: placeAbove ? ch - activeZone.y + margin : undefined,
                  width: cardWidth,
                  maxHeight,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  background: 'rgba(17, 24, 39, 0.97)',
                  color: '#fff',
                  borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
                  padding: 12,
                  fontSize: 12,
                  zIndex: 20,
                }}
              >
                <div
                  style={{
                    marginBottom: 8,
                    paddingBottom: 8,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
                    flexShrink: 0,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        background: severityColor[activeZone.severity ?? 'info'] ?? '#ffffff',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{activeZone.name}</span>
                    <span style={{ marginLeft: 'auto', opacity: 0.65, whiteSpace: 'nowrap' }}>
                      {activeZone.notices.length} · {activeZone.assetCount} assets
                    </span>
                  </div>
                  {activeZone.subtitle && (
                    <div style={{ opacity: 0.55, fontSize: 11, marginTop: 3, marginLeft: 17 }}>
                      {activeZone.subtitle}
                    </div>
                  )}
                </div>

                <div
                  style={{
                    overflowY: 'auto',
                    maxHeight: bodyMaxHeight,
                    marginRight: -4,
                    paddingRight: 4,
                  }}
                >
                  <div style={{ opacity: 0.5, fontSize: 10, letterSpacing: 0.6, marginBottom: 6 }}>
                    {activeZone.notices.length === 1 ? 'NOTICE' : 'NOTICES'}
                  </div>

                  {activeZone.notices.length === 0 && (
                    <div style={{ opacity: 0.7 }}>No notices available.</div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {activeZone.notices.map((n, i) => (
                      <div key={n.id ?? i}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                          <span
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              background: severityColor[n.severity ?? 'info'] ?? '#ffffff',
                              flexShrink: 0,
                              marginTop: 1,
                            }}
                          />
                          <span style={{ fontWeight: 600 }}>{n.title ?? 'Untitled notice'}</span>
                          {n.date && (
                            <span style={{ marginLeft: 'auto', opacity: 0.55, fontSize: 11 }}>
                              {n.date}
                            </span>
                          )}
                        </div>
                        {n.summary && (
                          <div style={{ opacity: 0.8, marginTop: 3, lineHeight: 1.4 }}>
                            {n.summary}
                          </div>
                        )}
                        <div
                          style={{
                            opacity: 0.6,
                            marginTop: 3,
                            fontSize: 11,
                            lineHeight: 1.4,
                          }}
                        >
                          {n.assets?.length ? (
                            <>
                              <span style={{ fontWeight: 600 }}>Affected Assets: </span>
                              {n.assets.join(' · ')}
                            </>
                          ) : (
                            <span style={{ fontStyle: 'italic' }}>
                              Zone-wide · no affected assets
                            </span>
                          )}
                        </div>
                        {n.url && (
                          <a
                            href={n.url}
                            onClick={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              navigate(`/notice/${n.id}`);
                            }}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-block',
                              marginTop: 4,
                              color: '#60a5fa',
                              textDecoration: 'none',
                            }}
                          >
                            View notice →
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
}
